import { useMemo } from 'react'
import { useAllBillingProfilesQuery } from '../../api/client-billing-profile-api-client'
import type { ParsedRow } from './bulkUpload.helpers'
import {
  indexProfilesByClientName,
  planRow,
  summarizePlans,
  type RowPlan,
} from './bulkUploadPlan'

interface UseBulkUploadPlanParams {
  rows: ParsedRow[]
  /** Only the preview step needs the comparison, so the fetch waits for it. */
  enabled: boolean
}

/** What each previewed row will do to its profile, recomputed as rows are edited. */
export const useBulkUploadPlan = ({
  rows,
  enabled,
}: UseBulkUploadPlanParams) => {
  const {
    data: profiles = [],
    isLoading,
    isError,
  } = useAllBillingProfilesQuery({
    enabled,
  })

  const profilesByName = useMemo(
    () => indexProfilesByClientName(profiles),
    [profiles]
  )

  const plansByRow = useMemo(() => {
    const plans = new Map<number, RowPlan>()
    // An account with nothing stored still needs planning: every row is new,
    // and one without recipients has to be caught. An unfinished or failed read
    // is unplannable: a failure is not an empty catalog, and planning against
    // it would call every stored company new.
    if (isLoading || isError) return plans
    for (const row of rows) {
      plans.set(
        row.rowNum,
        planRow(row, profilesByName.get(row.clientName.trim().toLowerCase()))
      )
    }
    return plans
  }, [rows, isLoading, isError, profilesByName])

  const planSummary = useMemo(
    () => summarizePlans(Array.from(plansByRow.values())),
    [plansByRow]
  )

  return {
    plansByRow,
    planSummary,
    isPlanLoading: enabled && isLoading,
    isPlanError: enabled && isError,
  }
}
